class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.config = window.cartDrawerConfig || {};
    this.busy = false;
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('change', this.handleChange.bind(this));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
    });
    document.addEventListener('submit', this.handleAddToCart.bind(this), true);
    window.addEventListener('resize', () => {
      if (this.classList.contains('is-open')) this.setOffset();
    });
  }

  connectedCallback() {
    this.bindHeader();
  }

  bindHeader() {
    document.querySelectorAll('[data-cart-drawer-toggle]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    const closeTriggers = ['#burger', '#search-toggle', '#localization', '#account', 'header .logo'];
    closeTriggers.forEach((selector) => {
      const el = document.querySelector(selector);
      if (!el) return;
      el.addEventListener('click', () => {
        if (this.classList.contains('is-open')) this.close();
      });
    });
  }

  setOffset() {
    const header = document.querySelector('header');
    const offset = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    this.style.setProperty('--cart-drawer-offset', offset + 'px');
  }

  open() {
    this.setOffset();
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    const closeBtn = this.querySelector('.cart-drawer__close');
    if (closeBtn) closeBtn.focus();
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
  }

  handleClick(e) {
    if (e.target.closest('[data-cart-drawer-close]')) {
      this.close();
      return;
    }
    const qtyBtn = e.target.closest('[data-cart-quantity-change]');
    if (qtyBtn) {
      const item = qtyBtn.closest('[data-line]');
      const input = item.querySelector('[data-cart-quantity-input]');
      const delta = parseInt(qtyBtn.dataset.cartQuantityChange, 10);
      let next = parseInt(input.value, 10) + delta;
      if (input.max && next > parseInt(input.max, 10)) next = parseInt(input.max, 10);
      if (next < 0) next = 0;
      this.changeLine(item.dataset.line, next);
      return;
    }
    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      const item = removeBtn.closest('[data-line]');
      this.changeLine(item.dataset.line, 0);
    }
  }

  handleChange(e) {
    const input = e.target.closest('[data-cart-quantity-input]');
    if (!input) return;
    const item = input.closest('[data-line]');
    let value = parseInt(input.value, 10);
    if (isNaN(value) || value < 0) value = 0;
    this.changeLine(item.dataset.line, value);
  }

  async handleAddToCart(e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action || !form.action.includes('/cart/add')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (this.busy) return;

    const submitBtn = form.querySelector('[type="submit"]');
    const formData = new FormData(form);
    formData.append('sections', 'cart-drawer');
    formData.append('sections_url', window.location.pathname);

    this.setBusy(true, submitBtn);
    try {
      const response = await fetch(this.config.addUrl, {
        method: 'POST',
        headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const data = await response.json();
      if (!response.ok) {
        this.showError(data.description || data.message || this.config.errorText);
        this.open();
        return;
      }
      this.render(data.sections['cart-drawer']);
      this.open();
    } catch (err) {
      this.showError(this.config.errorText);
      this.open();
    } finally {
      this.setBusy(false, submitBtn);
    }
  }

  async changeLine(line, quantity) {
    if (this.busy) return;
    this.setBusy(true);
    try {
      const response = await fetch(this.config.changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity, sections: 'cart-drawer', sections_url: window.location.pathname })
      });
      const data = await response.json();
      if (!response.ok) {
        this.showError(data.description || data.message || this.config.errorText);
        return;
      }
      this.render(data.sections['cart-drawer']);
    } catch (err) {
      this.showError(this.config.errorText);
    } finally {
      this.setBusy(false);
    }
  }

  async refresh() {
    try {
      const response = await fetch(`${this.config.rootUrl}?sections=cart-drawer`);
      const data = await response.json();
      this.render(data['cart-drawer']);
    } catch (err) {}
  }

  render(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('#CartDrawerContent');
    const current = this.querySelector('#CartDrawerContent');
    if (!fresh || !current) return;
    current.innerHTML = fresh.innerHTML;
    current.dataset.cartCount = fresh.dataset.cartCount;
    this.updateHeaderCount(parseInt(fresh.dataset.cartCount, 10) || 0);
    document.dispatchEvent(new CustomEvent('cart-drawer:updated'));
  }

  updateHeaderCount(count) {
    document.querySelectorAll('a.cart').forEach((link) => {
      let badge = link.querySelector('.cart-count');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('div');
          badge.className = 'cart-count';
          badge.id = 'CartCount';
          badge.setAttribute('aria-hidden', 'true');
          link.appendChild(badge);
        }
        badge.textContent = count;
      } else if (badge) {
        badge.remove();
      }
    });
  }

  setBusy(state, button) {
    this.busy = state;
    this.classList.toggle('is-busy', state);
    if (button) {
      button.disabled = state;
      button.classList.toggle('is-loading', state);
    }
  }

  showError(message) {
    const el = this.querySelector('#CartDrawerError');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(this.errorTimer);
    this.errorTimer = setTimeout(() => { el.hidden = true; }, 5000);
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

(function() {
  if (window.cartShippingConvertReady) return;
  window.cartShippingConvertReady = true;

  function getCurrency() {
    var currency = window.Shopify && window.Shopify.currency;
    if (!currency) return null;
    var rate = parseFloat(currency.rate) || 1;
    if (rate === 1) return null;
    return { rate: rate };
  }

  function parseMoneyFormat(sample) {
    var first = sample.search(/\d/);
    var lastMatch = sample.match(/\d(?!.*\d)/);
    if (first < 0 || !lastMatch) return null;

    var last = sample.lastIndexOf(lastMatch[0]);
    var body = sample.slice(first, last + 1);
    var digits = body.replace(/\D/g, '');
    var decimals = digits.length >= 9 ? 2 : 0;
    var decimalSeparator = decimals ? body.charAt(body.length - 3) : '';
    var thousandsSeparator = /\d/.test(body.charAt(1)) ? '' : body.charAt(1);

    return {
      prefix: sample.slice(0, first),
      suffix: sample.slice(last + 1),
      decimals: decimals,
      decimalSeparator: decimalSeparator,
      thousandsSeparator: thousandsSeparator
    };
  }

  function formatMoney(cents, format, forceWhole) {
    var decimals = forceWhole ? 0 : format.decimals;
    var fixed = (cents / 100).toFixed(decimals);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, format.thousandsSeparator);
    var amount = decimals ? whole + format.decimalSeparator + parts[1] : whole;
    return format.prefix + amount + format.suffix;
  }

  function convertAll() {
    var currency = getCurrency();
    if (!currency) return;

    var content = document.getElementById('CartDrawerContent');
    var sample = content ? content.getAttribute('data-money-sample') : '';
    var format = sample ? parseMoneyFormat(sample) : null;
    if (!format) return;

    document.querySelectorAll('[data-shipping-threshold]').forEach(function(el) {
      if (el.dataset.converted === 'true') return;
      var baseThreshold = parseInt(el.dataset.baseThreshold, 10) || 0;
      var localCents = Math.ceil((baseThreshold * currency.rate) / 100) * 100;
      el.dataset.converted = 'true';
      el.textContent = formatMoney(localCents, format, true);
    });

    var shipping = document.querySelector('[data-shipping-value]');
    if (shipping && shipping.dataset.converted !== 'true') {
      var baseCost = parseInt(shipping.dataset.baseCost, 10) || 0;
      var shippingThreshold = parseInt(shipping.dataset.baseThreshold, 10) || 0;
      var cartTotal = parseInt(shipping.dataset.cartTotal, 10) || 0;
      var localCost = Math.round(baseCost * currency.rate);
      var localThreshold = shippingThreshold * currency.rate;

      shipping.dataset.converted = 'true';

      if (shippingThreshold > 0 && cartTotal >= localThreshold) {
        shipping.innerHTML = formatMoney(0, format, false) + '&nbsp;&nbsp;<s>' + formatMoney(localCost, format, false) + '</s>';
      } else {
        shipping.textContent = formatMoney(localCost, format, false);
      }
    }
  }

  function init() {
    convertAll();

    var drawer = document.getElementById('CartDrawer');
    if (drawer && window.MutationObserver) {
      new MutationObserver(convertAll).observe(drawer, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();